import React, { useRef } from 'react'
import NextLink from 'next/link'
import {
  Box,
  Button,
  Center,
  Flex,
  Link,
  Text,
  useColorModeValue,
} from '@chakra-ui/react'

import { CALENDLY_LINK } from '@/constants'
import { ContainerBase } from '@/components/containers/ContainerBase'
import { NewsletterForm } from './NewsletterForm'
import { FeaturedPost } from './FeaturedPost'
import { CategoryPosts } from './CategoryPosts'
import { Follow } from './follow'
import { IGhostData } from '@/types/ghostData.interface'

type Props = {
  featuredVideo: string
  featuredPosts: IGhostData
  beginnerPosts: IGhostData
  advancedPosts: IGhostData
  institutionalPosts: IGhostData
  researchPosts: IGhostData
  downloadsPosts: IGhostData
}

export const HomePage = ({
  featuredVideo,
  featuredPosts,
  beginnerPosts,
  advancedPosts,
  institutionalPosts,
  researchPosts,
  downloadsPosts,
}: Props) => {
  const featuredPost = featuredPosts?.posts?.[0] || null
  const formRef = useRef<HTMLDivElement | null>(null)

  const focusEmailInput = () => {
    // find first input within subscribe form
    const input = formRef?.current?.getElementsByTagName('input')?.[0]
    // scroll to this input
    if (input) {
      input.scrollIntoView({ behavior: 'smooth' })
      input.focus()
    }
  }

  return (
    <Box bg={useColorModeValue('white', 'whiteAlpha.100')} id="featured">
      <ContainerBase
        py={{ base: 4, lg: 16 }}
        maxWidth={{ base: '100%', xl: '1280px' }}
      >
        {featuredPost && <FeaturedPost post={featuredPost} />}
        <Flex
          pt={{ base: 4, lg: 16 }}
          direction={{ base: 'column', lg: 'row' }}
          id="newsletter"
        >
          <Box mb={{ base: 4, lg: 0 }} flexGrow={1}>
            <Text
              variant="spaceGrotesk"
              fontSize="2xl"
              lineHeight="normal"
              mb={4}
            >
              Keep up with mining trends and insights with the Mining Memo
            </Text>
            <Box maxW={'676px'}>
              <NewsletterForm formRef={formRef} />
            </Box>
          </Box>
          <Box
            borderLeftWidth={{ base: 0, lg: '1px' }}
            borderTopWidth={{ base: '1px', lg: 0 }}
            borderColor={useColorModeValue('black', 'white')}
            ml={{ base: 0, lg: 16 }}
            pl={{ base: 0, lg: 6 }}
            pt={{ base: 6, lg: 0 }}
          >
            <Follow />
          </Box>
        </Flex>
      </ContainerBase>
      <Box bg={useColorModeValue('blackAlpha.100', 'black')} id="beginner">
        <ContainerBase
          py={{ base: 6, lg: 16, xl: 20 }}
          maxWidth={{ base: '100%', xl: '1280px' }}
        >
          <CategoryPosts
            title={'Getting Started'}
            description={'Essential guides for those new to Bitcoin mining.'}
            slug="beginner"
            button={
              <NextLink href="/hardware" passHref>
                <Button variant="brand" as="a">
                  Start Mining Today
                </Button>
              </NextLink>
            }
            posts={beginnerPosts.posts}
          />
        </ContainerBase>
      </Box>
      <Box id="video">
        <ContainerBase
          py={{ base: 6, lg: 16, xl: 20 }}
          maxWidth={{ base: '100%', xl: '1280px' }}
        >
          <Flex
            direction={{ base: 'column', lg: 'row' }}
            justifyContent="flex-start"
            alignItems={{ base: 'flex-start', lg: 'center' }}
          >
            <Box
              flexShrink={0}
              w="full"
              maxW={{ lg: '360px' }}
              mr={{ lg: '61px' }}
              mb={{ base: '40px', lg: 0 }}
              px={3}
            >
              <Link
                href="https://www.youtube.com/@CompassMining"
                isExternal
                color={useColorModeValue('black', 'white')}
                _hover={{
                  color: useColorModeValue('black', 'white'),
                  textDecoration: 'underline',
                }}
              >
                <Text
                  my={5}
                  fontSize="22px"
                  fontWeight={500}
                  lineHeight={1.36}
                  letterSpacing="-0.6px"
                >
                  Compass Mining Videos
                </Text>
                <Text fontSize="16px" lineHeight={1.28} letterSpacing="-0.2px">
                  Videos from inside our facilities, interviews with industry
                  leaders, and podcasts from the mining frontlines.
                </Text>
              </Link>
            </Box>
            <Box
              w="full"
              maxW="830px"
              borderWidth="1px"
              borderColor="whiteAlpha.500"
              boxShadow="0 1px 10px 0 rgba(0, 0, 0, 0.25)"
              bg="black.350"
              p={2}
            >
              <Box
                rounded="xl"
                w="full"
                overflow="hidden"
                style={{ width: '100%', aspectRatio: '16 / 9' }}
                __css={{
                  figure: {
                    width: '100%',
                    height: '100%',
                  },
                  iframe: {
                    width: '100%',
                    height: '100%',
                  },
                }}
              >
                <div
                  style={{ width: '100%', height: '100%' }}
                  dangerouslySetInnerHTML={{ __html: featuredVideo }}
                />
              </Box>
            </Box>
          </Flex>
        </ContainerBase>
      </Box>
      <Box bg={useColorModeValue('blackAlpha.100', 'black')} id="advanced">
        <ContainerBase
          py={{ base: 6, lg: 16, xl: 20 }}
          maxWidth={{ base: '100%', xl: '1280px' }}
        >
          <CategoryPosts
            title={'Advanced'}
            description={
              'Deep dives, operational strategies, and ASIC hardware reviews for active Bitcoin miners.'
            }
            slug="advanced"
            button={
              <NextLink href="/hardware" passHref>
                <Button variant="brand" as="a">
                  See Our Latest Hardware
                </Button>
              </NextLink>
            }
            posts={advancedPosts.posts}
          />
        </ContainerBase>
      </Box>
      <Box>
        <ContainerBase
          py={{ base: 6, lg: 16, xl: 20 }}
          maxWidth={{ base: '100%', xl: '1280px' }}
          id="institutional"
        >
          <CategoryPosts
            title={'Institutional'}
            description={
              'Insights for large-scale Bitcoin mining operations and institutional infrastructure.'
            }
            slug="institutional"
            button={
              <NextLink href="/contact-sales" passHref>
                <Button variant="brand" as="a">
                  Talk to Our Institutional Sales Team
                </Button>
              </NextLink>
            }
            posts={institutionalPosts.posts}
          />
        </ContainerBase>
      </Box>
      <Box bg={useColorModeValue('blackAlpha.100', 'black')} id="research">
        <ContainerBase
          py={{ base: 6, lg: 16, xl: 20 }}
          maxWidth={{ base: '100%', xl: '1280px' }}
        >
          <CategoryPosts
            title={'Research'}
            description={
              'Bitcoin mining market trends, thought leadership reports, and industry analysis.'
            }
            slug="research"
            button={
              <Button
                variant="brand"
                as="a"
                onClick={focusEmailInput}
                cursor="pointer"
              >
                Subscribe to Bitcoin Mining Updates
              </Button>
            }
            posts={researchPosts.posts}
          />
        </ContainerBase>
      </Box>
      <Box>
        <ContainerBase
          py={{ base: 6, lg: 16, xl: 20 }}
          maxWidth={{ base: '100%', xl: '1280px' }}
          id="downloads"
        >
          <CategoryPosts
            title={'Downloads'}
            description={
              'Free guides and in-depth reports for enterprise and retail miners.'
            }
            slug="downloads"
            posts={downloadsPosts.posts}
            stretchImage={false}
          />
        </ContainerBase>
      </Box>
      <Box bg="blackAlpha.900" position="relative" overflow="hidden" id="sales">
        <Box
          zIndex={1}
          position="absolute"
          top="-35%"
          left="50%"
          w="964px"
          h="964px"
          rounded="100%"
          background="radial-gradient(65.37% 65.37% at 52.49% 48.96%, #0060FF 0%, #00FFBD 100%);"
          filter="blur(200px)"
          transform={{ base: 'translateX(-10%)', md: 'translateX(-50%)' }}
        ></Box>
        <ContainerBase maxW={'946px'} py={8} position="relative" zIndex={2}>
          <Box
            p={{ base: 8, md: 16 }}
            rounded={'xl'}
            bg="blackAlpha.900"
            borderWidth="1px"
            borderColor="brand.600"
          >
            <Center
              flexDirection={'column'}
              justifyItems="center"
              minHeight={{ base: 0, md: '238px' }}
            >
              <Text
                variant="spaceGrotesk"
                fontSize={{ base: '24px', md: '32px' }}
                lineHeight="normal"
                color={'white'}
                fontWeight={500}
                mb={4}
                textAlign="center"
              >
                Have Questions About Bitcoin Mining?
              </Text>
              <Text
                fontSize={{ base: '20px', md: '24px' }}
                color={'whiteAlpha.800'}
                lineHeight={1.33}
                textAlign="center"
                mb={4}
              >
                Talk to our team and get personalized guidance for your mining
                goals.
              </Text>
              <Button as={Link} variant="brand" href={CALENDLY_LINK} isExternal>
                Schedule a Free Call
              </Button>
            </Center>
          </Box>
        </ContainerBase>
      </Box>
    </Box>
  )
}
